"use client";

import React, { useCallback, useEffect, useState } from "react";
import { useTicketEditMode } from "@/contexts/TicketEditModeContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

export function TaskDescriptionSection({ item }: TabContentProps<ContactTaskRow>) {
  const editMode = useTicketEditMode();
  const description = typeof item?.description === "string" ? item.description : "";
  const [localValue, setLocalValue] = useState<string>(description);

  useEffect(() => {
    const next = typeof item?.description === "string" ? item.description : "";
    setLocalValue(next);
  }, [item?.description]);

  const hasDirty =
    editMode?.isEditMode && localValue.trim() !== (description || "").trim();

  const handleConfirm = useCallback(() => {
    if (!editMode?.isEditMode) return;
    editMode.onUpdate({ description: localValue.trim() || null });
  }, [editMode, localValue]);

  const handleCancel = useCallback(() => {
    setLocalValue(description || "");
  }, [description]);

  const isEditMode = editMode?.isEditMode ?? false;

  if (!item) return null;

  return (
    <div className="space-y-3">
      <h4 className="text-md font-semibold text-card-foreground">Description</h4>
      <div className="bg-card-50 rounded-lg p-4 space-y-3">
        {isEditMode ? (
          <>
            <Textarea
              value={localValue}
              onChange={(e) => setLocalValue(e.target.value)}
              placeholder="No description provided."
              className="min-h-[120px] text-sm text-gray-700 dark:text-gray-300 resize-y"
              rows={5}
            />
            {hasDirty && (
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={handleConfirm}>
                  Confirm
                </Button>
                <Button size="sm" variant="outline" onClick={handleCancel}>
                  Cancel
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
            {description || "No description provided."}
          </div>
        )}
      </div>
    </div>
  );
}
